/**
 * resumenVentasAgregados — funciones PURAS de cálculo del "Resumen de ventas".
 *
 * Viven separadas del composable para poder testearlas sin montar Vue ni el
 * store. No formatean nada: devuelven números y los templates los pintan con
 * el filtro `formatear_precio` (así el formato de moneda sigue saliendo de
 * `config.CURRENCY` y no se duplica en dos sitios).
 *
 * Contrato del evento que entrega `back.events.resumen-ventas`:
 * {
 *   id, codigo, nombre_completo, fecha_inicio, teatro,
 *   taquilla:     { entradas_vendidas, singastos },
 *   online:       { entradas_vendidas, singastos },
 *   invitaciones: { entradas_vendidas, singastos }
 * }
 * `singastos` es el importe en euros del canal (las invitaciones siempre 0).
 */

/** Canales de venta del resumen, en el orden en que los muestra la tabla. */
export const CANALES = ['taquilla', 'online', 'invitaciones'];

/** Canales que facturan: las invitaciones son cortesía y no suman recaudación. */
export const CANALES_DE_PAGO = ['taquilla', 'online'];

const numero = valor => (typeof valor === 'number' && isFinite(valor) ? valor : 0);

/**
 * Datos de un canal de un evento/sesión, normalizados.
 *
 * @param {Object} fila Evento o sesión con los tres canales.
 * @param {string} canal 'taquilla' | 'online' | 'invitaciones'.
 * @returns {{ entradas: number, importe: number }}
 */
export function canalDe(fila, canal) {
    const datos = (fila && fila[canal]) || {};

    return {
        entradas: numero(datos.entradas_vendidas),
        importe: numero(datos.singastos)
    };
}

/**
 * Importe recaudado por un evento o sesión: taquilla + online.
 * Las invitaciones NO suman importe (van a 0).
 *
 * @param {Object} fila Evento o sesión.
 * @returns {number}
 */
export function importeFila(fila) {
    return CANALES_DE_PAGO.reduce((total, canal) => total + canalDe(fila, canal).importe, 0);
}

/**
 * Entradas emitidas por un evento o sesión: suma de los tres canales.
 *
 * @param {Object} fila Evento o sesión.
 * @returns {number}
 */
export function entradasFila(fila) {
    return CANALES.reduce((total, canal) => total + canalDe(fila, canal).entradas, 0);
}

/**
 * Acumulador vacío con la forma de un desglose por canal.
 *
 * @returns {Object}
 */
function acumuladorVacio() {
    const acc = { totalEntradas: 0, recaudacion: 0, eventos: 0 };

    CANALES.forEach(canal => {
        acc[canal] = { entradas: 0, importe: 0 };
    });

    return acc;
}

/**
 * Totales GLOBALES del rango filtrado (todas las páginas) tal como los puede
 * entregar el backend en `payload.totales`.
 *
 * Devuelve la MISMA forma que `totalesPagina` para que la UI no distinga de
 * dónde salen. Devuelve `null` si el objeto no viene o no trae los tres canales
 * con números válidos: en ese caso el llamador sigue sumando la página, así que
 * el backend puede desplegarse antes o después que el frontend.
 *
 * Contrato acordado con backend (tarea Asana 1219545939875545):
 * {
 *   "totales": {
 *     "recaudacion": 3088.10,           // opcional: si falta se deriva
 *     "total_entradas": 123,            // opcional: si falta se deriva
 *     "eventos": 20,                    // opcional: si falta se usa meta.total
 *     "taquilla":     { "entradas_vendidas": 52, "singastos": 1317.5 },
 *     "online":       { "entradas_vendidas": 66, "singastos": 1770.6 },
 *     "invitaciones": { "entradas_vendidas": 5,  "singastos": 0 }
 *   }
 * }
 *
 * @param {Object} payload Body del endpoint (`{ data, meta, totales? }`).
 * @returns {{
 *   taquilla: { entradas: number, importe: number },
 *   online: { entradas: number, importe: number },
 *   invitaciones: { entradas: number, importe: number },
 *   totalEntradas: number,
 *   recaudacion: number,
 *   eventos: number
 * }|null}
 */
export function totalesDesdeRespuesta(payload) {
    const totales = payload && payload.totales;

    if (!totales || typeof totales !== 'object') return null;

    const canales = {};

    for (let i = 0; i < CANALES.length; i++) {
        const canal = CANALES[i];
        const datos = totales[canal];

        if (!datos || typeof datos !== 'object') return null;
        if (typeof datos.entradas_vendidas !== 'number' || typeof datos.singastos !== 'number') return null;

        canales[canal] = { entradas: datos.entradas_vendidas, importe: datos.singastos };
    }

    const meta = (payload && payload.meta) || {};
    const eventos = typeof totales.eventos === 'number'
        ? totales.eventos
        : (typeof meta.total === 'number' ? meta.total : 0);

    return {
        ...canales,
        totalEntradas: typeof totales.total_entradas === 'number'
            ? totales.total_entradas
            : CANALES.reduce((suma, canal) => suma + canales[canal].entradas, 0),
        recaudacion: typeof totales.recaudacion === 'number'
            ? totales.recaudacion
            : canales.taquilla.importe + canales.online.importe,
        eventos
    };
}

/**
 * Estructura de totales en cero, con la misma forma que las otras dos.
 *
 * Es lo que muestran las tarjetas KPI mientras el backend no entregue
 * `totales`: se prefiere 0 explícito antes que la suma de la página, que
 * parecería el total del rango sin serlo.
 *
 * @returns {{ taquilla: Object, online: Object, invitaciones: Object, totalEntradas: number, recaudacion: number, eventos: number }}
 */
export function totalesVacios() {
    const totales = acumuladorVacio();

    totales.eventos = 0;

    return totales;
}

/**
 * Totales consolidados de la página: los que alimentan la fila de totales del
 * pie ("Totales de esta página").
 *
 * IMPORTANTE: son totales de la PÁGINA cargada, no del rango. Las tarjetas KPI
 * NO los usan: muestran `totales` (el global del rango) y, mientras el backend
 * no lo entregue, ceros.
 *
 * IMPORTANTE: son totales de la PÁGINA cargada, no del rango filtrado. El
 * backend solo devuelve los totales por evento paginados, así que un total
 * global exigiría un agregado de backend que hoy no existe. La UI lo dice
 * explícitamente ("en pantalla") para no mentirle al usuario.
 *
 * @param {Array<Object>} eventos Filas de la página actual.
 * @returns {{
 *   taquilla: { entradas: number, importe: number },
 *   online: { entradas: number, importe: number },
 *   invitaciones: { entradas: number, importe: number },
 *   totalEntradas: number,
 *   recaudacion: number,
 *   eventos: number
 * }}
 */
export function totalesPagina(eventos) {
    const lista = Array.isArray(eventos) ? eventos : [];
    const totales = acumuladorVacio();

    lista.forEach(evento => {
        CANALES.forEach(canal => {
            const datos = canalDe(evento, canal);
            totales[canal].entradas += datos.entradas;
            totales[canal].importe += datos.importe;
        });

        totales.totalEntradas += entradasFila(evento);
        totales.recaudacion += importeFila(evento);
    });

    totales.eventos = lista.length;

    return totales;
}

/**
 * Reparto porcentual de unos valores sobre su total, en décimas, con la suma
 * EXACTA de 100 cuando hay total.
 *
 * El diseño mostraba los porcentajes de taquilla y online y no llegaban a 100:
 * el denominador es el total de entradas emitidas (los tres canales) pero sólo
 * se pintaban dos. Aquí se reparte el porcentaje de TODOS los valores que se
 * pasen, y el residuo del redondeo se le suma al mayor para que la suma sea
 * exactamente 100 y no 99,9.
 *
 * Devuelve `null` en cada posición cuando no hay total (sin entradas no hay
 * porcentaje que mostrar: la UI pinta un guion, no un 0% que es mentira).
 *
 * @param {Array<number>} valores
 * @returns {Array<number|null>}
 */
export function porcentajesSobreTotal(valores) {
    const lista = Array.isArray(valores) ? valores : [];
    const total = lista.reduce((suma, valor) => suma + numero(valor), 0);

    if (!total) return lista.map(() => null);

    const porcentajes = lista.map(valor => Math.round((numero(valor) / total) * 1000) / 10);
    const resto = Math.round((100 - porcentajes.reduce((suma, p) => suma + p, 0)) * 10) / 10;

    if (resto !== 0) {
        const mayor = porcentajes.indexOf(Math.max(...porcentajes));
        porcentajes[mayor] = Math.round((porcentajes[mayor] + resto) * 10) / 10;
    }

    return porcentajes;
}
